import React, { createContext, useState, useContext, useEffect, useRef, useMemo } from 'react';
import { LocalizationContextBuilder, createAsyncMessageBundleManager } from '@amzn/arb-tools';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

// import arbManifest from '../../build/panther/arbManifest';
// import domLocalizationContextProvider from '../utils/domLocalizationContextProvider';
// import {createAsyncMessageBundleManager} from "@amzn/arb-tools";
// TODO: Wishlist: Be able to create a message bundle manager without arguments
// Very hard to have a sensible default mbm creator
// const mbm = createAsyncMessageBundleManager({
//   arbManifest: {},
//   localizationContextProvider: domLocalizationContextProvider,
//   urlPrefix: 'translations',
//   resolveBundleUrl: (...args) => {
//     console.log(args)
//     return ''
//   },
// });
var MbmContext = createContext({});
MbmContext.displayName = "MbmContext";

/**
 * React context for LocalizationContext.
 */
var LocalizationContextContext = createContext({
    /**
     * Provide a localization context with default values — this simplifies
     * working with the API but will be overwritten by a required
     * `defaultLocalizationContext` object immediately when the context object
     * is created with {@link LocalizationContextProvider}.
     */
    localizationContext: new LocalizationContextBuilder().build(),
    setLocalizationContext: function () { },
});
LocalizationContextContext.displayName = "LocalizationContextContext";

/**
 * Creates a LocalizationContext context object that can be consumed and updated
 * via {@link useLocalizationContext}.
 *
 * @param defaultLocalizationContext
 * @param children
 * @constructor
 */
var LocalizationContextProvider = function (_a) {
    var defaultLocalizationContext = _a.localizationContext, children = _a.children;
    var _b = __read(useState(defaultLocalizationContext), 2), localizationContext = _b[0], setLocalizationContext = _b[1];
    return (React.createElement(LocalizationContextContext.Provider, { value: {
            localizationContext: localizationContext,
            setLocalizationContext: function (localizationContext) {
                setLocalizationContext(localizationContext);
            },
        } }, children));
};

/**
 * This hook allows getting an instance of the MessageBundleManager that is
 * provided by the `MbmProvider`.
 */
function useMbm() {
    return useContext(MbmContext);
}
/**
 * Request a MessageBundle using the MessageBundleManager that is provided by
 * the `MbmProvider`.
 *
 * @param bundleName fully qualified bundle name
 * @param context localization context to use
 */
function useBundle(bundleName, context) {
    var _this = this;
    var _a = __read(useState({
        isLoading: true,
        bundle: undefined,
    }), 2), state = _a[0], setState = _a[1];
    var mbm = useMbm();
    var localizationContext = useLocalizationContext().localizationContext;
    useEffect(function () {
        (function () { return __awaiter(_this, void 0, void 0, function () {
            var loadedBundle;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        setState({ isLoading: true, bundle: undefined });
                        return [4 /*yield*/, mbm.getBundle(bundleName, context)];
                    case 1:
                        loadedBundle = _a.sent();
                        setState({ isLoading: false, bundle: loadedBundle });
                        return [2 /*return*/];
                }
            });
        }); })();
        // TODO: this is a cheap-man's hash - improve LCs comparability
    }, [bundleName, JSON.stringify(context), JSON.stringify(localizationContext)]);
    // @ts-ignore
    return [state.bundle, state.isLoading];
}
/**
 * Get the `LocalizationContext` instance that is provided by the
 * `LocalizationContextProvider`.
 */
function useLocalizationContext() {
    return useContext(LocalizationContextContext);
}

/**
 * Reactive MbmProvider that utilizes the `LocalizationContextProvider`.
 * @param props
 * @constructor
 */
function LcConsumingMbmProvider(props) {
    var localizationContext = useLocalizationContext().localizationContext;
    var localizationContextRef = useRef(localizationContext);
    localizationContextRef.current = localizationContext;
    var mbm = useMemo(function () {
        return createAsyncMessageBundleManager(__assign(__assign({}, props), { localizationContextProvider: function () {
                return localizationContextRef.current;
            } }));
    }, []);
    return React.createElement(MbmContext.Provider, { value: mbm }, props.children);
}

/**
 * Provide a MessageBundleManager instance downstream via `useMbm()` &
 * `useBundle()`.
 *
 * Accepts either a constructed MessageBundleManager instance or options for
 * constructing a MessageBundleManager instance. Later is recommended, as it
 * wraps the provider in a stateful LocalizationContextProvider making the
 * component reactive to localization context changes.
 *
 * @param props
 * @constructor
 */
var MbmProvider = function (props) {
    if (hasMbm(props)) {
        return React.createElement(MbmContext.Provider, { value: props.mbm }, props.children);
    }
    return (React.createElement(LocalizationContextProvider, { localizationContext: props.defaultLocalizationContext },
        React.createElement(LcConsumingMbmProvider, __assign({}, props), props.children)));
};
/**
 * Helper method to parse component props
 * @param mbmOrMessageBundleManagerOptions
 */
function hasMbm(mbmOrMessageBundleManagerOptions) {
    return mbmOrMessageBundleManagerOptions.mbm !== undefined;
}

// TODO: add Suspense with fallback loader
var withBundle = function (bundleName) {
    return function (WrappedComponent) {
        var displayName = WrappedComponent.displayName || WrappedComponent.name || "Component";
        // Creating the inner component. The calculated Props type here is the where the magic happens.
        var ComponentWithBundle = function (props) {
            // Fetch the props you want to inject. This could be done with context instead.
            var _a = __read(useBundle(bundleName, props.localizationContext), 2), loadedBundle = _a[0], isLoading = _a[1];
            if (isLoading) {
                return React.createElement("div", null, "loading");
            }
            // props comes afterwards so the can override the default ones.
            return React.createElement(WrappedComponent, __assign({}, props, { bundle: loadedBundle }));
        };
        ComponentWithBundle.displayName = "withBundle(" + displayName + ")";
        return ComponentWithBundle;
    };
};

export { LocalizationContextProvider, MbmProvider, useBundle, useLocalizationContext, useMbm, withBundle };
